// Read-side tolerance for product-line booleans. BE writes booleans, but data
// round-tripping through ArcadeDB / cross-org RPCs has historically surfaced
// as the string "true" / "false" in some payloads. Strict `=== true` would
// silently mis-categorize those reps, so any helper that reads user-supplied
// product-line flags must go through these so the shape mismatch can't bite.
// `isTrue` is exported because direct-read call sites (e.g., AccountsView's
// per-account filter, RightSidebar's org nav gating) hit productLines fields
// outside the helpers above and need the same tolerance.
export const isTrue = (v) => v === true || v === "true";
const isFalse = (v) => v === false || v === "false";

/**
 * Derived flags from auth.orgInfo.productLines for use across manufacturer-side UI.
 *
 * Legacy permissive behavior: when `productLines` is null/undefined/empty, treat
 * the org as DME-only. This matches the existing fallback in RightSidebar.js
 * (legacy orgs that pre-date the productLines schema).
 */
export function getProductLineFlags(productLines) {
  const hasDme = productLines == null || Object.keys(productLines).length === 0 ? true : isTrue(productLines.dme);
  const hasPeptides = isTrue(productLines?.peptides);
  return {
    hasDme,
    hasPeptides,
    dmeOnly: hasDme && !hasPeptides,
    peptidesOnly: !hasDme && hasPeptides,
    dualLine: hasDme && hasPeptides,
  };
}

/**
 * Per-user effective productLines = org capability ∩ user capability.
 * Mirrors the inviter-capability pattern in OnboardingView so non-supervisor
 * reps only see the lines they themselves are authorized to sell, even when
 * their org sells both.
 *
 *   - Supervisors bypass per-user gating (org capability passthrough).
 *   - Legacy reps without `custom.productLines` fall through to org capability.
 *   - Otherwise: DME requires `dme !== false` (opt-out, legacy default ON);
 *     peptides requires `peptides === true` (opt-in, legacy default OFF).
 *
 * Returns a `{ dme, peptides }` object suitable for feeding into the other
 * helpers in this file (getProductLineFlags, getInitialAccountsLine, etc).
 */
export function getEffectiveProductLines(auth, { isSupervisor = false } = {}) {
  const orgFlags = getProductLineFlags(auth?.orgInfo?.productLines);
  if (isSupervisor) {
    return { dme: orgFlags.hasDme, peptides: orgFlags.hasPeptides };
  }
  const user = auth?.user?.custom?.productLines;
  // Treat missing OR empty object as legacy (org passthrough) so a partial
  // BE migration writing `{}` doesn't silently strip peptides from a rep who
  // used to have it. Mirrors the empty-object handling in getProductLineFlags.
  //
  // Per-KEY presence check on partially-written objects: a sparse `{dme:false}`
  // (no `peptides` key) should NOT silently drop peptides — keys missing from
  // the user record fall through to the org's capability. Only an explicit
  // `peptides: false` (or `dme: false`) revokes a line.
  const userUnset = !user || Object.keys(user).length === 0;
  const userCanSellDme = userUnset || !("dme" in user) || !isFalse(user.dme);
  const userCanSellPeptides = userUnset || ("peptides" in user ? isTrue(user.peptides) : orgFlags.hasPeptides);
  return {
    dme: orgFlags.hasDme && userCanSellDme,
    peptides: orgFlags.hasPeptides && userCanSellPeptides,
  };
}

/**
 * What product lines the current user can grant when inviting/creating a
 * provider (OnboardingView) or sub-rep (UserEdit). Two ceilings stack:
 *
 *   - org ceiling: nobody (including supervisors) can grant a product line
 *     the org itself doesn't sell, set via Profile → Product Lines.
 *   - per-user ceiling: a non-supervisor rep can only grant lines they
 *     themselves can sell (mirrors the tier-ceiling rule). Supervisors and
 *     legacy reps without `custom.productLines` set fall through.
 *
 * The supervisor short-circuit on the per-user check is intentional — the
 * 2026-05-20 rollback memo documents why changing it broke the
 * manufacturer-admin onboarding flow. Org-level revocation still wins via
 * the org-cap intersection here.
 *
 * Returns `{ canSellDme, canSellPeptides }`. Both are booleans.
 */
export function getInviterCapability(auth, { isSupervisor = false } = {}) {
  const orgFlags = getProductLineFlags(auth?.orgInfo?.productLines);
  const inviterProductLines = auth?.user?.custom?.productLines;
  const userCanSellDme = isSupervisor || !inviterProductLines || !isFalse(inviterProductLines.dme);
  const userCanSellPeptides = isSupervisor || !inviterProductLines || isTrue(inviterProductLines.peptides);
  return {
    canSellDme: orgFlags.hasDme && userCanSellDme,
    canSellPeptides: orgFlags.hasPeptides && userCanSellPeptides,
  };
}

/**
 * Initial value for AccountsView's product-line tab. Peptides-only orgs land
 * on the peptide tab; everyone else starts on DME.
 */
export function getInitialAccountsLine(productLines) {
  const { peptidesOnly } = getProductLineFlags(productLines);
  return peptidesOnly ? "peptide" : "dme";
}

/**
 * Initial product-line checkbox state for the sales-rep referral link builder.
 * Defaults to checking only the lines the org actually has.
 */
export function getInitialOnboardingState(productLines) {
  const { hasDme, hasPeptides } = getProductLineFlags(productLines);
  return { dme: hasDme, peptides: hasPeptides };
}

/**
 * Demo-mode entry role. Peptides-only orgs enter as a peptide provider;
 * everyone else enters as a DME provider (current default behavior).
 */
export function getDefaultDemoRole(productLines) {
  const { peptidesOnly } = getProductLineFlags(productLines);
  return peptidesOnly ? "peptidesProvider" : "provider";
}

/**
 * STRICT effective product lines — same return shape as
 * `getEffectiveProductLines`, but treats partially-written user records
 * as opt-out for missing keys instead of preserving org capability.
 *
 * Why this exists: the BE provider-signup process writes ONLY the truthy
 * `custom.productLines` keys — a peptides-only sales rep saved via that
 * path gets `{peptides: true}` with no `dme` key. The canonical helper's
 * "missing key = preserve org" rule (intentional for partial UserEdit
 * updates) treats those reps as still authorized for DME on a dual-line
 * org, leaking DME UI everywhere the dashboard filters through
 * `getEffectiveProductLines`. This variant closes that gap at the
 * call site without changing the canonical helper, which has its own
 * tested invariants (see `sub-rep-onboarding-canonical-helper-trap`
 * and the deliberate `sparse {peptides:true} keeps DME at org capability`
 * test in this file).
 *
 *   - Supervisors bypass per-user gating (org capability passthrough).
 *     `useAuth.hasGroup("supervisor")` short-circuits true for
 *     `role === "administrator"`, so admins inherit the bypass.
 *   - Reps with no `custom.productLines` set (or empty `{}`) still pass
 *     through to org capability — pre-per-user-lines grandfathered.
 *   - Otherwise (any explicit key set): strict intersection — each present
 *     key must be truthy (`true` or string `"true"`, see `isTrue` above);
 *     missing keys count as opt-out at the user level.
 *
 * Use this in any dashboard/UI surface that filters "what THIS user
 * sees." Don't use for partial-update write paths — the canonical
 * helper's missing-key tolerance is correct for those.
 */
export function getStrictEffectiveProductLines(auth, { isSupervisor = false } = {}) {
  const orgFlags = getProductLineFlags(auth?.orgInfo?.productLines);
  if (isSupervisor) return { dme: orgFlags.hasDme, peptides: orgFlags.hasPeptides };
  const userLines = auth?.user?.custom?.productLines;
  const userHasExplicitKeys = userLines && typeof userLines === "object" && Object.keys(userLines).length > 0;
  if (!userHasExplicitKeys) return { dme: orgFlags.hasDme, peptides: orgFlags.hasPeptides };
  return {
    dme: orgFlags.hasDme && isTrue(userLines.dme),
    peptides: orgFlags.hasPeptides && isTrue(userLines.peptides),
  };
}

/**
 * STRICT inviter capability — same return shape as `getInviterCapability`
 * (`{canSellDme, canSellPeptides}`), but uses the strict semantics from
 * `getStrictEffectiveProductLines`. Closes the same BE-signup sparse-shape
 * gap on the grant-side (OnboardingView's referral form, UserEdit's
 * sub-rep checkboxes): a peptides-only inviter with sparse
 * `{peptides:true}` should not be able to grant a new provider/sub-rep
 * the DME line just because the org sells both.
 */
export function getStrictInviterCapability(auth, { isSupervisor = false } = {}) {
  const { dme, peptides } = getStrictEffectiveProductLines(auth, { isSupervisor });
  return { canSellDme: dme, canSellPeptides: peptides };
}

/**
 * Product-line lens for demo mode — captured at enterDemo so the role
 * switcher keeps showing the right buckets after the fake demo persona
 * has replaced auth.user / auth.orgInfo. Thin wrapper that pulls
 * `isSupervisor` out of useAuth's `hasGroup` predicate so demo callers
 * don't need to compute it inline.
 */
export function getDemoLensProductLines(auth, hasGroup) {
  const isSupervisor = typeof hasGroup === "function" && hasGroup("supervisor");
  return getStrictEffectiveProductLines(auth, { isSupervisor });
}

/**
 * Filter DemoRoleSwitcher groups to lines the org has enabled. Each group
 * must have a `key` of "dme" or "peptides".
 */
export function filterDemoRoleGroups(groups, productLines) {
  const { hasDme, hasPeptides } = getProductLineFlags(productLines);
  return groups.filter((g) => {
    if (g.key === "dme") return hasDme;
    if (g.key === "peptides") return hasPeptides;
    return true;
  });
}
